import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    TextField,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardContent,
    Chip,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    Phone as PhoneIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    Note as NoteIcon,
    Delete as DeleteIcon,
    DeleteForever as DeleteForeverIcon,
    UploadFile as UploadFileIcon,
    CloudUpload as CloudUploadIcon,
    ContentCopy as ContentCopyIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = 
  process.env.REACT_APP_API_URL || "http://31.97.50.160:5001/api";

interface TeleChallanData {
    _id: string;
    originalChallanId: string;
    originalUserId: string;
    challanData: {
        challanId: string;
        amount: number;
        paid: boolean;
        dueDate: string;
        createdAt: string;
    };
    userData: {
        fullName: string;
        rollNumber: string;
        phone: string;
        email: string;
        city: string;
    };
    status: string;
    assignedDate: string;
    notes?: {
        text: string;
        date: string;
        admin: string;
        _id: string;
    }[];
}

const TeleChallan = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const [teleChallans, setTeleChallans] = useState<TeleChallanData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Pagination and Filter states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Add Dialog states
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [addResult, setAddResult] = useState<{ added: number, skipped: number } | null>(null);

    // Add Filter states
    const [daysPreset, setDaysPreset] = useState<string>('30'); // Default 30 days
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Notes Dialog states
    const [openNotesDialog, setOpenNotesDialog] = useState(false);
    const [selectedChallan, setSelectedChallan] = useState<TeleChallanData | null>(null);
    const [newNote, setNewNote] = useState('');
    const [noteLoading, setNoteLoading] = useState(false);
    
    // Delete Dialog states
    const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [openDeleteSelectedDialog, setOpenDeleteSelectedDialog] = useState(false);
    const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);

    // Remove Dialog states
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [removeResult, setRemoveResult] = useState<{ removed: number } | null>(null);
    const [removeDaysPreset, setRemoveDaysPreset] = useState<string>('30');
    const [removeStartDate, setRemoveStartDate] = useState('');
    const [removeEndDate, setRemoveEndDate] = useState('');

    // Upload Dialog states
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadStats, setUploadStats] = useState<{
        totalRows: number;
        usersCreated: number;
        usersMatched: number;
        challansCreated: number;
        challansMatched: number;
        teleChallansAdded: number;
        teleChallansSkipped: number;
        skippedInvalid: number;
        notAdded?: {
            row: number;
            challanId: string;
            rollNumber: string;
            fullName: string;
            reason: string;
        }[];
        errors: string[];
    } | null>(null);
    const [uploadError, setUploadError] = useState('');
    const [notAddedSearch, setNotAddedSearch] = useState('');
    const [notAddedPage, setNotAddedPage] = useState(0);
    const [notAddedRowsPerPage, setNotAddedRowsPerPage] = useState(10);

    const fetchTeleChallans = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/tele-challans`, {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    search,
                    status: statusFilter
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setTeleChallans(response.data.data.teleChallans);
                setTotalCount(response.data.data.pagination.total);
            }
        } catch (err) {
            console.error('Error fetching tele-challans:', err);
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeleChallans();
    }, [page, rowsPerPage, search, statusFilter]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddChallans = async () => {
        setAddLoading(true);
        setAddResult(null);
        try {
            const token = localStorage.getItem('token');
            const payload: any = {};

            if (startDate || endDate) {
                if (startDate) payload.startDate = startDate;
                if (endDate) payload.endDate = endDate;
            } else {
                payload.days = daysPreset;
            }

            const response = await axios.post(`${API_URL}/tele-challans/add`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setAddResult(response.data.data);
                fetchTeleChallans(); // Refresh list
                setSuccess(response.data.message);
            }
        } catch (err) {
            console.error('Error adding challans:', err);
            setError('Failed to add challans');
        } finally {
            setAddLoading(false);
        }
    };

    const handleOpenNotes = (challan: TeleChallanData) => {
        setSelectedChallan(challan);
        setNewNote('');
        setOpenNotesDialog(true);
    };

    const handleAddNote = async () => {
        if (!selectedChallan || !newNote.trim()) return;

        setNoteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_URL}/tele-challans/${selectedChallan._id}/notes`,
                { note: newNote },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // Update local state
                const updatedChallan = response.data.data;
                setTeleChallans(prev => prev.map(c =>
                    c._id === updatedChallan._id ? updatedChallan : c
                ));
                setSelectedChallan(updatedChallan);
                setNewNote('');
            }
        } catch (err) {
            console.error('Error adding note:', err);
            // Optionally show error toast
        } finally {
            setNoteLoading(false);
        }
    };
    
    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this Tele Challan?')) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_URL}/tele-challans/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setSuccess('Tele Challan deleted successfully');
                fetchTeleChallans(); // Refresh list
            }
        } catch (err) {
            console.error('Error deleting tele-challan:', err);
            setError('Failed to delete tele-challan');
        }
    };
    
    const handleDeleteAll = async () => {
        setDeleteAllLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_URL}/tele-challans/delete/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setSuccess(response.data.message);
                setOpenDeleteAllDialog(false);
                fetchTeleChallans(); // Refresh list
            }
        } catch (err) {
            console.error('Error deleting all tele-challans:', err);
            setError('Failed to delete all data');
        } finally {
            setDeleteAllLoading(false);
        }
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedIds = teleChallans.map((item) => item._id);
            setSelectedIds(newSelectedIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: string) => {
        const selectedIndex = selectedIds.indexOf(id);
        let newSelectedIds: string[] = [];

        if (selectedIndex === -1) {
            newSelectedIds = [...selectedIds, id];
        } else {
            newSelectedIds = selectedIds.filter((item) => item !== id);
        }

        setSelectedIds(newSelectedIds);
    };

    const handleDeleteSelected = async () => {
        setDeleteSelectedLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/tele-challans/bulk-delete`, {
                ids: selectedIds
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setSuccess(response.data.message);
                setOpenDeleteSelectedDialog(false);
                setSelectedIds([]);
                fetchTeleChallans(); // Refresh list
            }
        } catch (err) {
            console.error('Error deleting selected tele-challans:', err);
            setError('Failed to delete selected data');
        } finally {
            setDeleteSelectedLoading(false);
        }
    };

    const handleRemoveChallans = async () => {
        setRemoveLoading(true);
        setRemoveResult(null);
        try {
            const token = localStorage.getItem('token');
            const payload: any = {};

            if (removeStartDate || removeEndDate) {
                if (removeStartDate) payload.startDate = removeStartDate;
                if (removeEndDate) payload.endDate = removeEndDate;
            } else {
                payload.days = removeDaysPreset;
            }

            const response = await axios.post(`${API_URL}/tele-challans/remove`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setRemoveResult(response.data.data);
                fetchTeleChallans(); // Refresh list
                setSuccess(response.data.message);
            }
        } catch (err) {
            console.error('Error removing challans:', err);
            setError('Failed to remove challans');
        } finally {
            setRemoveLoading(false);
        }
    };

    const getStatusChip = (status: string) => {
        const colors: any = {
            pending: 'warning',
            called: 'info',
            resolved: 'success',
            unreachable: 'error'
        };
        return <Chip label={status.toUpperCase()} color={colors[status] || 'default'} size="small" />;
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile) {
            setUploadError('Please select an Excel (.xlsx, .xls) or CSV file first');
            return;
        }

        setUploadLoading(true);
        setUploadError('');
        setUploadStats(null);
        setNotAddedSearch('');
        setNotAddedPage(0);

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', uploadFile);

            const response = await axios.post(`${API_URL}/tele-challans/upload-file`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setUploadStats(response.data.data);
                setSuccess(response.data.message);
                fetchTeleChallans();
            } else {
                setUploadError(response.data.message || 'Failed to process file');
            }
        } catch (err: any) {
            console.error('Error uploading file:', err);
            setUploadError(err.response?.data?.message || err.message || 'Failed to upload file');
        } finally {
            setUploadLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Tele Challan List
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<UploadFileIcon />}
                        onClick={() => {
                            setOpenUploadDialog(true);
                            setUploadStats(null);
                            setUploadError('');
                            setUploadFile(null);
                            setNotAddedSearch('');
                            setNotAddedPage(0);
                        }}
                    >
                        Upload Excel / CSV
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAddDialog(true)}
                    >
                        Add Unpaid Challans
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => {
                                setOpenRemoveDialog(true);
                                setRemoveResult(null);
                            }}
                        >
                            Remove Unpaid Challans
                        </Button>
                    )}
                </Box>
                {isAdmin && selectedIds.length > 0 && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setOpenDeleteSelectedDialog(true)}
                        sx={{ ml: 2 }}
                    >
                        Delete Selected ({selectedIds.length})
                    </Button>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Search User/Challan/Phone"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                endAdornment: <SearchIcon color="action" />
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            SelectProps={{ native: true }}
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="called">Called</option>
                            <option value="resolved">Resolved</option>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTeleChallans} fullWidth>
                            Refresh List
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Main Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {isAdmin && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={
                                            selectedIds.length > 0 && selectedIds.length < teleChallans.length
                                        }
                                        checked={teleChallans.length > 0 && selectedIds.length === teleChallans.length}
                                        onChange={handleSelectAll}
                                        disabled={loading}
                                    />
                                </TableCell>
                            )}
                            <TableCell>User Name</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Challan ID</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Payment</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Assigned Date</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : teleChallans.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                                    No records found. Click "Add Unpaid Challans" to start.
                                </TableCell>
                            </TableRow>
                        ) : (
                            teleChallans.map((item) => (
                                <TableRow key={item._id} hover selected={selectedIds.indexOf(item._id) !== -1}>
                                    {isAdmin && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedIds.indexOf(item._id) !== -1}
                                                onChange={() => handleSelectOne(item._id)}
                                                disabled={loading}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{item.userData.fullName}</Typography>
                                        <Typography variant="caption" color="textSecondary">{item.userData.rollNumber}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                            {item.userData.phone}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{item.challanData.challanId}</TableCell>
                                    <TableCell>Rs. {item.challanData.amount}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.challanData.paid ? "Paid" : "Unpaid"}
                                            color={item.challanData.paid ? "success" : "error"}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {item.challanData.dueDate ? new Date(item.challanData.dueDate).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell>{getStatusChip(item.status)}</TableCell>
                                    <TableCell>{new Date(item.assignedDate).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Badge badgeContent={item.notes?.length || 0} color="primary">
                                            <IconButton size="small" onClick={() => handleOpenNotes(item)}>
                                                <NoteIcon />
                                            </IconButton>
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {isAdmin && (
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={() => handleDelete(item._id)}
                                                title="Delete"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </TableContainer>

            {/* Add Dialog */}
            <Dialog open={openAddDialog} onClose={() => !addLoading && setOpenAddDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Unpaid Challans</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Select Timeframe</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            {[7, 30, 60].map(days => (
                                <Button
                                    key={days}
                                    variant={daysPreset === String(days) && !startDate ? "contained" : "outlined"}
                                    onClick={() => {
                                        setDaysPreset(String(days));
                                        setStartDate('');
                                        setEndDate('');
                                    }}
                                >
                                    Last {days} Days
                                </Button>
                            ))}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>Or Custom Range</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="From"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setDaysPreset(''); // Clear preset if custom used
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="To"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setDaysPreset('');
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {addResult && (
                            <Alert severity="success" sx={{ mt: 3 }}>
                                Operation Complete! Added: <b>{addResult.added}</b>, Skipped: <b>{addResult.skipped}</b> (duplicates)
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)} disabled={addLoading}>Close</Button>
                    <Button
                        onClick={handleAddChallans}
                        variant="contained"
                        disabled={addLoading}
                        startIcon={addLoading && <CircularProgress size={20} color="inherit" />}
                    >
                        {addLoading ? 'Adding...' : 'Add to List'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remove Dialog */}
            <Dialog open={openRemoveDialog} onClose={() => !removeLoading && setOpenRemoveDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Remove Unpaid Challans</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Select Timeframe</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            {[7, 30, 60].map(days => (
                                <Button
                                    key={days}
                                    variant={removeDaysPreset === String(days) && !removeStartDate ? "contained" : "outlined"}
                                    onClick={() => {
                                        setRemoveDaysPreset(String(days));
                                        setRemoveStartDate('');
                                        setRemoveEndDate('');
                                    }}
                                >
                                    Last {days} Days
                                </Button>
                            ))}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>Or Custom Range</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="From"
                                    InputLabelProps={{ shrink: true }}
                                    value={removeStartDate}
                                    onChange={(e) => {
                                        setRemoveStartDate(e.target.value);
                                        setRemoveDaysPreset(''); // Clear preset if custom used
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="To"
                                    InputLabelProps={{ shrink: true }}
                                    value={removeEndDate}
                                    onChange={(e) => {
                                        setRemoveEndDate(e.target.value);
                                        setRemoveDaysPreset('');
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {removeResult && (
                            <Alert severity="success" sx={{ mt: 3 }}>
                                Operation Complete! Removed: <b>{removeResult.removed}</b> unpaid records
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRemoveDialog(false)} disabled={removeLoading}>Close</Button>
                    <Button
                        onClick={handleRemoveChallans}
                        variant="contained"
                        color="error"
                        disabled={removeLoading}
                        startIcon={removeLoading && <CircularProgress size={20} color="inherit" />}
                    >
                        {removeLoading ? 'Removing...' : 'Remove from List'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notes Dialog */}
            <Dialog open={openNotesDialog} onClose={() => setOpenNotesDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Notes for {selectedChallan?.userData.fullName}
                    <Typography variant="body2" color="textSecondary">
                        Challan ID: {selectedChallan?.challanData.challanId}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* History */}
                        <Box sx={{ maxHeight: 300, overflowY: 'auto', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                            {selectedChallan?.notes && selectedChallan.notes.length > 0 ? (
                                selectedChallan.notes.map((note) => (
                                    <Paper key={note._id} sx={{ p: 1.5, mb: 1.5 }}>
                                        <Typography variant="body2">{note.text}</Typography>
                                        <Box display="flex" justifyContent="space-between" mt={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                By: {note.admin || 'Admin'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(note.date).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary" align="center">
                                    No notes yet.
                                </Typography>
                            )}
                        </Box>

                        {/* Add new note */}
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Type a new note..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNotesDialog(false)}>Close</Button>
                    <Button
                        onClick={handleAddNote}
                        variant="contained"
                        disabled={!newNote.trim() || noteLoading}
                    >
                        {noteLoading ? 'Saving...' : 'Add Note'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Selected Confirmation Dialog */}
            <Dialog open={openDeleteSelectedDialog} onClose={() => !deleteSelectedLoading && setOpenDeleteSelectedDialog(false)}>
                <DialogTitle>Delete Selected Data?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        Are you sure you want to permanently delete the {selectedIds.length} selected Tele Challan records? This action cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteSelectedDialog(false)} disabled={deleteSelectedLoading}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteSelected} 
                        color="error" 
                        variant="contained"
                        disabled={deleteSelectedLoading}
                        startIcon={deleteSelectedLoading && <CircularProgress size={20} color="inherit" />}
                    >
                        {deleteSelectedLoading ? 'Deleting...' : 'Yes, Delete Selected'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete All Confirmation Dialog */}
            <Dialog open={openDeleteAllDialog} onClose={() => !deleteAllLoading && setOpenDeleteAllDialog(false)}>
                <DialogTitle>Delete All Data?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        This action cannot be undone. This will permanently delete ALL Tele Challan records from the database.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteAllDialog(false)} disabled={deleteAllLoading}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteAll} 
                        color="error" 
                        variant="contained"
                        disabled={deleteAllLoading}
                        startIcon={deleteAllLoading && <CircularProgress size={20} color="inherit" />}
                    >
                        {deleteAllLoading ? 'Deleting...' : 'Yes, Delete All'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Excel/CSV Dialog */}
            <Dialog
                open={openUploadDialog}
                onClose={() => !uploadLoading && setOpenUploadDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UploadFileIcon color="primary" />
                    Upload Users & Challans (Excel / CSV)
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <b>Safe Import:</b> Any challan already present in the Tele Challan list will remain <b>completely untouched and undisturbed</b>. Only new records will be safely imported, creating users and challans in the database where needed.
                    </Alert>

                    {uploadError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
                            {uploadError}
                        </Alert>
                    )}

                    <Box
                        sx={{
                            border: '2px dashed #90caf9',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            bgcolor: 'action.hover',
                            cursor: 'pointer',
                            mb: 3
                        }}
                    >
                        <input
                            accept=".xlsx, .xls, .csv"
                            style={{ display: 'none' }}
                            id="excel-challan-file-upload"
                            type="file"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) {
                                    setUploadFile(f);
                                    setUploadError('');
                                    setUploadStats(null);
                                }
                            }}
                        />
                        <label htmlFor="excel-challan-file-upload" style={{ cursor: 'pointer', display: 'block', width: '100%' }}>
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6">
                                {uploadFile ? uploadFile.name : 'Click to select Excel (.xlsx, .xls) or CSV file'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {uploadFile ? `${(uploadFile.size / 1024).toFixed(1)} KB` : 'Supports challans export files with student & challan details'}
                            </Typography>
                        </label>
                    </Box>

                    {uploadLoading && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <CircularProgress size={40} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Processing file and importing records safely...
                            </Typography>
                        </Box>
                    )}

                    {uploadStats && (
                        <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#fbfbfb' }}>
                            <Typography variant="h6" gutterBottom color="success.main">
                                Import Completed Successfully
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={{ xs: 6, sm: 4 }}>
                                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                        <Typography variant="caption" color="text.secondary">Total Rows</Typography>
                                        <Typography variant="h5" fontWeight="bold">{uploadStats.totalRows}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 4 }}>
                                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                                        <Typography variant="caption" color="text.secondary">Added to Tele List</Typography>
                                        <Typography variant="h5" fontWeight="bold" color="success.main">
                                            +{uploadStats.teleChallansAdded}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 4 }}>
                                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fff3e0' }}>
                                        <Typography variant="caption" color="text.secondary">Existing Untouched</Typography>
                                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                                            {uploadStats.teleChallansSkipped}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>Users:</b> {uploadStats.usersCreated} created, {uploadStats.usersMatched} matched
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>Challans:</b> {uploadStats.challansCreated} created, {uploadStats.challansMatched} matched
                                    </Typography>
                                </Grid>
                            </Grid>

                            {uploadStats.errors && uploadStats.errors.length > 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="caption" component="div"><b>Warnings:</b></Typography>
                                    {uploadStats.errors.map((e: string, idx: number) => (
                                        <Typography key={idx} variant="caption" component="div">• {e}</Typography>
                                    ))}
                                </Alert>
                            )}
                        </Paper>
                    )}

                    {/* Detailed list of Challans that were NOT added */}
                    {uploadStats && uploadStats.notAdded && uploadStats.notAdded.length > 0 && (() => {
                        const q = notAddedSearch.toLowerCase().trim();
                        const filtered = uploadStats.notAdded.filter((item) => {
                            if (!q) return true;
                            return (
                                (item.challanId && item.challanId.toLowerCase().includes(q)) ||
                                (item.fullName && item.fullName.toLowerCase().includes(q)) ||
                                (item.rollNumber && item.rollNumber.toLowerCase().includes(q)) ||
                                (item.reason && item.reason.toLowerCase().includes(q))
                            );
                        });

                        return (
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BlockIcon color="warning" />
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Challans Not Added ({uploadStats.notAdded.length})
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => {
                                            const ids = uploadStats.notAdded!.map(i => i.challanId).filter(Boolean).join('\n');
                                            navigator.clipboard.writeText(ids);
                                            setSuccess(`Copied ${uploadStats.notAdded!.length} Challan IDs to clipboard`);
                                        }}
                                    >
                                        Copy Challan IDs
                                    </Button>
                                </Box>

                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Filter not-added records by Challan ID, Student Name, Roll No, or Reason..."
                                    value={notAddedSearch}
                                    onChange={(e) => {
                                        setNotAddedSearch(e.target.value);
                                        setNotAddedPage(0);
                                    }}
                                    InputProps={{
                                        endAdornment: <SearchIcon color="action" fontSize="small" />
                                    }}
                                    sx={{ mb: 1.5 }}
                                />

                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '70px' }}>Row #</TableCell>
                                                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Challan ID</TableCell>
                                                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Student Name</TableCell>
                                                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Roll Number</TableCell>
                                                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Reason</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filtered
                                                .slice(notAddedPage * notAddedRowsPerPage, notAddedPage * notAddedRowsPerPage + notAddedRowsPerPage)
                                                .map((item, idx) => (
                                                    <TableRow key={idx} hover>
                                                        <TableCell>{item.row}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: 'primary.main' }}>
                                                            {item.challanId}
                                                        </TableCell>
                                                        <TableCell>{item.fullName}</TableCell>
                                                        <TableCell>{item.rollNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={item.reason}
                                                                color={item.reason.includes('Already') ? 'warning' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            {filtered.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        No records matching "{notAddedSearch}"
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={filtered.length}
                                    page={notAddedPage}
                                    onPageChange={(e, newP) => setNotAddedPage(newP)}
                                    rowsPerPage={notAddedRowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setNotAddedRowsPerPage(parseInt(e.target.value, 10));
                                        setNotAddedPage(0);
                                    }}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                />
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUploadDialog(false)} disabled={uploadLoading}>
                        {uploadStats ? 'Done' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleUploadSubmit}
                        variant="contained"
                        color="success"
                        disabled={!uploadFile || uploadLoading}
                        startIcon={uploadLoading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
                    >
                        {uploadLoading ? 'Processing...' : 'Process & Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TeleChallan;
